/**
 * Register Store - State management for Register.tsx page
 *
 * Related page: frontend/src/pages/Register.tsx
 *
 * Uses the "complex form" pattern with formErrorsAtom for field-level validation.
 * See LoginStore.ts for the same pattern.
 */

import { atom } from 'jotai'
import { authClient } from '../client'
import { FormError } from 'txom-frontend-libs'

// ============================================================================
// FORM STATE
// ============================================================================

const initialRegisterForm = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  isSubmitting: false,
}

type RegisterForm = typeof initialRegisterForm

export const registerFormAtom = atom(initialRegisterForm)

// ============================================================================
// FORM ERRORS
// - null: form has never been submitted, don't show errors
// - object: form has been submitted, show errors and validate in real-time
// ============================================================================

type RegisterFormErrors = {
  form?: string
  name?: string
  email?: string
  password?: string
  confirmPassword?: string
}

export const registerFormErrorsAtom = atom<RegisterFormErrors | null>(null)

// ============================================================================
// VALIDATION
// ============================================================================

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function hasFieldErrors(errors: RegisterFormErrors): boolean {
  const { form: _form, ...fieldErrors } = errors
  return Object.keys(fieldErrors).length > 0
}

const validateRegisterFormAtom = atom(null, (get, set) => {
  const form = get(registerFormAtom)
  const errors: RegisterFormErrors = {}

  if (!form.name.trim()) {
    errors.name = 'Name is required'
  }

  if (!form.email.trim()) {
    errors.email = 'Email is required'
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Invalid email format'
  }

  if (!form.password) {
    errors.password = 'Password is required'
  } else if (form.password.length < 8) {
    errors.password = 'Password must be at least 8 characters'
  }

  if (!form.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
  } else if (form.password && form.confirmPassword !== form.password) {
    errors.confirmPassword = 'Passwords do not match'
  }

  set(registerFormErrorsAtom, errors)
  return hasFieldErrors(errors)
})

const setRegisterFormErrorAtom = atom(null, (get, set, err: unknown, fallback: string) => {
  const message = err instanceof FormError ? err.message : (err instanceof Error ? err.message || fallback : fallback)
  set(registerFormErrorsAtom, (current) => ({ ...(current ?? {}), form: message }))
})

// ============================================================================
// FORM ACTIONS
// ============================================================================

export const updateRegisterFormAtom = atom(null, (get, set, updates: Partial<RegisterForm>) => {
  const current = get(registerFormAtom)
  set(registerFormAtom, { ...current, ...updates })

  // If form has been submitted before (errors !== null), validate in real-time
  if (get(registerFormErrorsAtom) !== null) {
    set(validateRegisterFormAtom)
  }
})

// Register action atom - returns email on success for redirect to verify page
export const submitRegisterAtom = atom(null, async (get, set): Promise<string> => {
  const form = get(registerFormAtom)

  // Validate and enable real-time validation mode
  if (set(validateRegisterFormAtom)) {
    throw new FormError('Please fix the errors above')
  }

  set(registerFormAtom, { ...form, isSubmitting: true })

  try {
    const res = await authClient.signUp.email({
      email: form.email,
      password: form.password,
      name: form.name,
    })
    if (res.error) {
      throw new FormError(res.error.message || 'Sign up failed')
    }
    return form.email
  } catch (err) {
    set(setRegisterFormErrorAtom, err, 'Registration failed')
    throw err
  } finally {
    set(registerFormAtom, (current) => ({ ...current, isSubmitting: false }))
  }
})

// OAuth register action atom (same as sign in)
export const oAuthRegisterAtom = atom(null, async (_get, set) => {
  try {
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: window.location.origin + '/auth/callback',
    })
  } catch (err) {
    set(setRegisterFormErrorAtom, err, 'OAuth sign up failed')
    throw err
  }
})

// ============================================================================
// ROUTE LOADERS
// ============================================================================

type Store = ReturnType<typeof import('jotai').createStore>

export async function loader(_store: Store) {
  return null
}
